import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

export default function SubscriptionPage() {
  const [plans, setPlans] = useState({});
  const [allBooks, setAllBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [bookSearch, setBookSearch] = useState({});

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, booksRes] = await Promise.all([
        adminAPI.getSubscriptions(),
        adminAPI.getBooks({ limit: 5000 }) // Fetch all books for selection
      ]);
      setPlans(subRes.data || {});
      setAllBooks(booksRes.data || []);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSubscriptions(plans);
      showToast('Subscription rules updated successfully! ✓');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const setBooksForPlan = (planKey, nextBooks) => {
    setPlans(prev => ({
      ...prev,
      [planKey]: { ...prev[planKey], books: nextBooks }
    }));
  };

  const addBook = (planKey, bookId) => {
    if (!bookId) return;
    const current = plans[planKey]?.books || [];
    const bid = String(bookId);
    if (!current.some(id => String(id) === bid)) {
      setBooksForPlan(planKey, [...current, bid]);
    }
  };

  const removeBook = (planKey, bookId) => {
    setBooksForPlan(planKey, (plans[planKey]?.books || []).filter(id => String(id) !== String(bookId)));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: 16 }}>
        <div style={{ width: 40, height: 40, border: '3px solid #E8E4DF', borderTopColor: '#C97B77', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
        <div style={{ color: '#888899', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5 }}>Synchronizing Registry...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 20px', maxWidth: 1400, margin: '0 auto', fontFamily: 'Nunito,sans-serif' }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, flexWrap: 'wrap', gap: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display,serif', fontSize: 32, fontWeight: 700, color: '#2C2C35', marginBottom: 6 }}>
            Subscription <em style={{ color: '#C97B77', fontStyle: 'italic' }}>Plans</em>
          </h1>
          <p style={{ fontSize: 14, color: '#888899', maxWidth: 600 }}>
            Curate individual book lists for each tier. Selected books will be standard for all users on these plans.
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '14px 32px', background: '#2C2C35', color: 'white', border: 'none', borderRadius: 14,
            fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: '0 8px 24px rgba(44,44,53,0.15)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 10
          }}
        >
          {saving ? 'Saving...' : 'Save & Publish Rules'}
          {!saving && <span>→</span>}
        </button>
      </div>

      {/* ── Plans Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 28 }}>
        {Object.entries(plans).map(([key, plan]) => {
          const isFullAccess = (plan.books || []).includes('*');
          const planBooks = isFullAccess ? allBooks : allBooks.filter(b =>
            (plan.books || []).some(id => String(id) === String(b._id))
          );
          const searchVal = (bookSearch[key] || '').toLowerCase();

          const searchResults = searchVal.length >= 2
            ? allBooks.filter(b =>
              !planBooks.some(pb => String(pb._id) === String(b._id)) &&
              decrypt(b.book_name || b.book_title).toLowerCase().includes(searchVal)
            ).slice(0, 10)
            : [];

          return (
            <div key={key} style={{
              background: 'white', borderRadius: 28, border: '1px solid #E8E4DF',
              display: 'flex', flexDirection: 'column', minHeight: 600, overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.02)'
            }}>

              {/* Header */}
              <div style={{ padding: '32px 32px 24px', borderBottom: '1px solid #F0ECE8', background: isFullAccess ? '#FDF8F7' : 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#C97B77', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>PLAN TIER</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#2C2C35', fontFamily: 'Playfair Display,serif' }}>{plan.name}</div>
                  </div>
                  <div style={{
                    padding: '6px 12px', background: isFullAccess ? '#EBF4F0' : '#F9F7F5',
                    color: isFullAccess ? '#7A9E8E' : '#2C2C35', borderRadius: 30, fontSize: 11, fontWeight: 800
                  }}>
                    {isFullAccess ? 'ALL' : (plan.books || []).length} BOOKS
                  </div>
                </div>
              </div>

              {/* Books Management */}
              <div style={{ padding: 28, flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Search & Add */}
                {!isFullAccess && (
                  <div style={{ position: 'relative' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#888899', letterSpacing: 0.5, marginBottom: 8, textTransform: 'uppercase' }}>Add Books to Plan</div>
                    <input
                      type="text"
                      placeholder="Search to add book..."
                      value={bookSearch[key] || ''}
                      onChange={(e) => setBookSearch(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{
                        width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #F0ECE8',
                        fontSize: 14, fontFamily: 'Nunito,sans-serif', outline: 'none', boxSizing: 'border-box'
                      }}
                    />
                    {searchResults.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                        background: 'white', border: '1px solid #E8E4DF', borderRadius: 12,
                        marginTop: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.1)', maxHeight: 250, overflowY: 'auto'
                      }}>
                        {searchResults.map(b => (
                          <div key={b._id} onClick={() => addBook(key, b._id)}
                            style={{ padding: '10px 16px', fontSize: 13, borderBottom: '1px solid #F0ECE8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 14 }}>📘</span>
                            <span style={{ flex: 1 }}>{decrypt(b.book_name || b.book_title)}</span>
                            <span style={{ fontSize: 10, color: '#C97B77', fontWeight: 700 }}>+ ADD</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Status Toggle */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                  <button
                    onClick={() => setBooksForPlan(key, isFullAccess ? [] : ['*'])}
                    style={{
                      flex: 1, padding: '10px', borderRadius: 12, border: 'none',
                      background: isFullAccess ? '#E8B4B1' : '#2C2C35',
                      color: 'white', fontWeight: 700, fontSize: 11, cursor: 'pointer', textTransform: 'uppercase'
                    }}>
                    {isFullAccess ? 'Switch to Individual Selection' : '⚡ Grant Full Access (All Books)'}
                  </button>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid #F0ECE8' }} />

                {/* Book List Container */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#888899', letterSpacing: 0.5, marginBottom: 12, textTransform: 'uppercase' }}>
                    {isFullAccess ? 'Full Catalog Access' : `Assigned Books (${(plan.books || []).length})`}
                  </div>

                  <div style={{
                    flex: 1, background: '#F9F7F5', borderRadius: 20, padding: 16, border: '1px solid #F0ECE8',
                    overflowY: 'auto', maxHeight: 350
                  }}>
                    {isFullAccess ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7A9E8E' }}>
                        <div style={{ fontSize: 24, marginBottom: 10 }}>🌟</div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>All current and future books are included in this plan.</div>
                      </div>
                    ) : (plan.books || []).length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {planBooks.map(book => (
                          <div key={book._id} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'white',
                            borderRadius: 10, border: '1px solid #F0ECE8'
                          }}>
                            <span style={{ fontSize: 14 }}>📘</span>
                            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#555567', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {decrypt(book.book_name || book.book_title)}
                            </span>
                            <button onClick={() => removeBook(key, book._id)}
                              style={{ border: 'none', background: 'transparent', color: '#EE8E8E', cursor: 'pointer', fontSize: 16, fontWeight: 700, padding: '0 4px' }}>
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#AAAABC', fontSize: 12 }}>
                        No individual books assigned. Search above to add titles.
                      </div>
                    )}
                  </div>
                </div>

              </div>

            </div>
          );
        })}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#B04C49' : '#4A6157',
          color: 'white', padding: '12px 24px', borderRadius: 14, fontWeight: 700,
          fontSize: 13, zIndex: 100, boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
        }}>
          {toast.msg}
        </div>
      )}

    </div>
  );
}
